import React, { useState, useEffect, useRef } from "react";
import { Carousel } from "react-bootstrap";
import VideoEmbed from "./VideoEmbed";
import ReactGA from "react-ga";

/**
 * ImageGallery Component
 * Supports mixed media: images and videos in a carousel
 * 
 * @param {Array} images - Array of image URLs or objects: [{type: 'image', url: '...'}, {type: 'video', url: '...', videoType: 'youtube', title: '...'}]
 * @param {string} alt - Alt text for images
 * @param {number} initialIndex - Starting index in the gallery
 * @param {string} projectId - Project ID for hash navigation
 */
const ImageGallery = ({ images, alt, initialIndex = 0, projectId = null }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [carouselHeight, setCarouselHeight] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [imageOpacity, setImageOpacity] = useState({});
  const carouselItemRef = useRef(null);
  const carouselContainerRef = useRef(null);
  const imageRefs = useRef({});
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50; // Minimum distance in pixels for a swipe

  // Normalize images array: convert strings to objects, handle mixed media
  const normalizeMedia = (mediaArray) => {
    if (!mediaArray || mediaArray.length === 0) return [];
    
    return mediaArray.map((item, index) => {
      if (typeof item === 'string') {
        // Legacy format: just a URL string, assume it's an image
        return { type: 'image', url: item, index };
      } else if (item && item.type) {
        // Already normalized object
        return { ...item, index };
      }
      return { type: 'image', url: item, index };
    });
  };

  const mediaItems = normalizeMedia(images);

  // Update active index when initialIndex changes (from hash navigation)
  useEffect(() => {
    if (initialIndex >= 0 && initialIndex < mediaItems.length) {
      setActiveIndex(initialIndex);
    }
  }, [initialIndex, mediaItems.length]);

  // Update hash when image changes (if projectId is provided)
  useEffect(() => {
    if (projectId && mediaItems.length > 1) {
      // Convert 0-based index to 1-based for URL (more user-friendly)
      const imageNum = activeIndex + 1;
      const newHash = imageNum === 1 
        ? `#${projectId}` // First image: just project ID
        : `#${projectId}-${imageNum}`; // Other images: project-id-number
      
      // Only update if hash is different to avoid loops
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, '', newHash);
      }
    }

    // Track gallery navigation (only if there's more than 1 item and we have a project ID)
    if (projectId && mediaItems.length > 1 && activeIndex >= 0) {
      const currentItem = mediaItems[activeIndex];
      const mediaType = currentItem?.type === 'video' ? 'video' : 'image';
      const totalMedia = mediaItems.length;
      const videoCount = mediaItems.filter(item => item.type === 'video').length;
      const imageCount = mediaItems.filter(item => item.type === 'image').length;
      ReactGA.event({
        category: 'project_modal',
        action: 'gallery_navigated',
        label: `${projectId}|${mediaType}_${activeIndex + 1}_of_${totalMedia}|total_images_${imageCount}_videos_${videoCount}|navigation_method_auto`,
        value: activeIndex + 1 // Image/video number
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, projectId, mediaItems.length]); // mediaItems structure can change but length is sufficient

  // Check for already-loaded images (cached) on mount and when media items change
  useEffect(() => {
    mediaItems.forEach((item, index) => {
      if (item.type === 'image' && imageRefs.current[index]) {
        const img = imageRefs.current[index];
        if (img.complete) {
          // Image is already cached/loaded, mark it as loaded
          setLoadedImages(prev => {
            if (!prev.has(index)) {
              setImageOpacity(prevOpacity => ({ ...prevOpacity, [index]: 1 }));
              return new Set([...prev, index]);
            }
            return prev;
          });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaItems]);

  // Keyboard navigation handler - only works when carousel has focus or is in modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle arrow keys if we're not typing in an input/textarea
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );

      if (isInputFocused) return;

      // Handle arrow keys for gallery navigation
      if (e.key === "ArrowLeft" && mediaItems.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : mediaItems.length - 1;
          // Track keyboard navigation
          if (projectId) {
            const currentItem = mediaItems[newIndex];
            const mediaType = currentItem?.type === 'video' ? 'video' : 'image';
            ReactGA.event({
              category: 'project_modal',
              action: 'gallery_navigated',
              label: `${projectId}|${mediaType}_${newIndex + 1}_of_${mediaItems.length}|navigation_method_keyboard_left_arrow`,
              value: newIndex + 1
            });
          }
          return newIndex;
        });
      } else if (e.key === "ArrowRight" && mediaItems.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        setActiveIndex((prev) => {
          const newIndex = prev < mediaItems.length - 1 ? prev + 1 : 0;
          // Track keyboard navigation
          if (projectId) {
            const currentItem = mediaItems[newIndex];
            const mediaType = currentItem?.type === 'video' ? 'video' : 'image';
            ReactGA.event({
              category: 'project_modal',
              action: 'gallery_navigated',
              label: `${projectId}|${mediaType}_${newIndex + 1}_of_${mediaItems.length}|navigation_method_keyboard_right_arrow`,
              value: newIndex + 1
            });
          }
          return newIndex;
        });
      }
    };

    // Add keyboard event listener to window (works even without focus)
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mediaItems.length]);

  // Focus carousel container when modal opens or active index changes (for accessibility)
  useEffect(() => {
    if (carouselContainerRef.current && mediaItems.length > 1) {
      // Small delay to ensure modal is fully rendered
      const timeoutId = setTimeout(() => {
        if (carouselContainerRef.current) {
          carouselContainerRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [mediaItems.length, activeIndex]);

  // Mobile swipe gesture handlers
  const handleTouchStart = (e) => {
    if (mediaItems.length <= 1) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (mediaItems.length <= 1) return;
    
    const swipeDistance = touchStartX.current - touchEndX.current;
    
    // Check if swipe distance is significant enough
    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swiped left - go to next
        setActiveIndex((prev) => {
          const newIndex = prev < mediaItems.length - 1 ? prev + 1 : 0;
          // Track swipe gesture
          if (projectId) {
            const currentItem = mediaItems[newIndex];
            const mediaType = currentItem?.type === 'video' ? 'video' : 'image';
            ReactGA.event({
              category: 'project_modal',
              action: 'gallery_navigated',
              label: `${projectId}|${mediaType}_${newIndex + 1}_of_${mediaItems.length}|navigation_method_swipe_left`,
              value: newIndex + 1
            });
          }
          return newIndex;
        });
      } else {
        // Swiped right - go to previous
        setActiveIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : mediaItems.length - 1;
          // Track swipe gesture
          if (projectId) {
            const currentItem = mediaItems[newIndex];
            const mediaType = currentItem?.type === 'video' ? 'video' : 'image';
            ReactGA.event({
              category: 'project_modal',
              action: 'gallery_navigated',
              label: `${projectId}|${mediaType}_${newIndex + 1}_of_${mediaItems.length}|navigation_method_swipe_right`,
              value: newIndex + 1
            });
          }
          return newIndex;
        });
      }
    }
    
    // Reset touch positions
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Smooth height transition when image changes
  useEffect(() => {
    const updateHeight = () => {
      if (imageRefs.current[activeIndex]) {
        const img = imageRefs.current[activeIndex];
        if (img.complete) {
          // Image is already loaded
          const container = img.parentElement;
          if (container) {
            const newHeight = container.offsetHeight;
            setCarouselHeight(newHeight);
          }
        } else {
          // Wait for image to load
          img.onload = () => {
            const container = img.parentElement;
            if (container) {
              const newHeight = container.offsetHeight;
              setCarouselHeight(newHeight);
            }
          };
        }
      }
    };

    // Small delay to allow Bootstrap carousel transition to start
    const timeoutId = setTimeout(updateHeight, 50);
    return () => clearTimeout(timeoutId);
  }, [activeIndex]);

  if (!images || images.length === 0) {
    return null;
  }

  // Handle image load
  const handleImageLoad = (index) => {
    setLoadedImages(prev => new Set([...prev, index]));
    // Fade in the image smoothly
    setImageOpacity(prev => ({ ...prev, [index]: 1 }));
  };

  // Loading placeholder/spinner component
  const LoadingPlaceholder = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        minHeight: "300px",
        backgroundColor: "#f8f9fa",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "4px solid #e9ecef",
            borderTop: "4px solid #7c3aed",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px",
          }}
        />
        <div style={{ color: "#6c757d", fontSize: "0.9rem" }}>Loading image...</div>
      </div>
    </div>
  );

  // If only one media item, show it without carousel controls
  if (mediaItems.length === 1) {
    const item = mediaItems[0];
    
    // If it's a video, render video embed
    if (item.type === 'video') {
      return (
        <div className="mb-3">
          <VideoEmbed 
            url={item.url} 
            type={item.videoType || 'youtube'} 
            title={item.title} 
          />
        </div>
      );
    }
    
    // It's an image
    const isLoaded = loadedImages.has(0);
    const opacity = imageOpacity[0] ?? 0;
    
    return (
      <div className="mb-3" style={{ position: "relative", minHeight: "300px" }}>
        {!isLoaded && <LoadingPlaceholder />}
        <img
          ref={(el) => {
            if (el) {
              imageRefs.current[0] = el;
              // Check if image is already cached/loaded
              if (el.complete && !loadedImages.has(0)) {
                handleImageLoad(0);
              }
            }
          }}
          src={item.url}
          alt={alt || "Project image"}
          className="img-fluid rounded"
          style={{
            width: "100%",
            maxHeight: "500px",
            objectFit: "contain",
            opacity: opacity,
            transition: "opacity 0.3s ease-in-out",
            display: isLoaded ? "block" : "none",
          }}
          loading="eager"
          onLoad={() => handleImageLoad(0)}
        />
      </div>
    );
  }

  // Custom arrow icons
  const PrevIcon = () => (
    <div
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.2s",
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </div>
  );

  const NextIcon = () => (
    <div
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.2s",
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </div>
  );

  return (
    <div className="mb-3">
      <style>
        {`
          .carousel-control-prev:hover div,
          .carousel-control-next:hover div {
            background-color: rgba(0, 0, 0, 0.9) !important;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        ref={carouselItemRef}
        style={{
          height: carouselHeight ? `${carouselHeight}px` : 'auto',
          transition: 'height 0.4s ease-in-out',
          overflow: 'hidden',
        }}
      >
        <div
          ref={carouselContainerRef}
          tabIndex={images.length > 1 ? 0 : -1}
          style={{ outline: 'none' }}
          aria-label="Image gallery - use arrow keys to navigate"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Carousel
            activeIndex={activeIndex}
            onSelect={(selectedIndex) => {
              setActiveIndex(selectedIndex);
              // Track carousel control button navigation
              if (projectId && selectedIndex !== activeIndex) {
                const currentItem = mediaItems[selectedIndex];
                const mediaType = currentItem?.type === 'video' ? 'video' : 'image';
                const navigationMethod = selectedIndex > activeIndex ? 'carousel_next_button' : 'carousel_prev_button';
                ReactGA.event({
                  category: 'project_modal',
                  action: 'gallery_navigated',
                  label: `${projectId}|${mediaType}_${selectedIndex + 1}_of_${mediaItems.length}|navigation_method_${navigationMethod}`,
                  value: selectedIndex + 1
                });
              }
            }}
            interval={null} // Disable auto-play
            indicators={false} // We'll use thumbnails instead
            controls={mediaItems.length > 1}
            prevIcon={<PrevIcon />}
            nextIcon={<NextIcon />}
          >
          {mediaItems.map((item, index) => {
            if (item.type === 'video') {
              // Render video item
              return (
                <Carousel.Item key={index}>
                  <div
                    style={{
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    <VideoEmbed 
                      url={item.url} 
                      type={item.videoType || 'youtube'} 
                      title={item.title} 
                    />
                  </div>
                </Carousel.Item>
              );
            }
            
            // Render image item
            const isLoaded = loadedImages.has(index);
            const opacity = imageOpacity[index] ?? 0;
            
            return (
              <Carousel.Item key={index}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "300px",
                    backgroundColor: "#f8f9fa",
                    position: "relative",
                  }}
                >
                  {!isLoaded && <LoadingPlaceholder />}
                  <img
                    ref={(el) => {
                      if (el) imageRefs.current[index] = el;
                    }}
                    className="d-block"
                    src={item.url}
                    alt={`${alt || "Project image"} - ${index + 1}`}
                    style={{
                      maxHeight: "500px",
                      maxWidth: "100%",
                      objectFit: "contain",
                      height: "auto",
                      width: "auto",
                      opacity: opacity,
                      transition: "opacity 0.3s ease-in-out",
                      display: isLoaded ? "block" : "none",
                      position: isLoaded ? "relative" : "absolute",
                    }}
                    loading={index === 0 ? "eager" : "lazy"}
                    onLoad={() => {
                      handleImageLoad(index);
                      // Update height after image loads
                      if (index === activeIndex && imageRefs.current[index]) {
                        const img = imageRefs.current[index];
                        const container = img.parentElement;
                        if (container) {
                          const newHeight = container.offsetHeight;
                          setCarouselHeight(newHeight);
                        }
                      }
                    }}
                  />
                </div>
              </Carousel.Item>
            );
          })}
          </Carousel>
        </div>
      </div>
      
      {/* Thumbnail row */}
      {mediaItems.length > 1 && (
        <div className="mt-3">
          <div className="d-flex justify-content-center flex-wrap gap-2">
            {mediaItems.map((item, index) => (
              <div
                key={index}
                onClick={() => {
                  setActiveIndex(index);
                  // Track thumbnail click navigation
                  if (projectId && index !== activeIndex) {
                    const currentItem = mediaItems[index];
                    const mediaType = currentItem?.type === 'video' ? 'video' : 'image';
                    ReactGA.event({
                      category: 'project_modal',
                      action: 'gallery_navigated',
                      label: `${projectId}|${mediaType}_${index + 1}_of_${mediaItems.length}|navigation_method_thumbnail_click`,
                      value: index + 1
                    });
                  }
                }}
                style={{
                  cursor: "pointer",
                  border: activeIndex === index ? "3px solid #7c3aed" : "3px solid transparent",
                  borderRadius: "4px",
                  padding: "2px",
                  transition: "border-color 0.2s",
                  opacity: activeIndex === index ? 1 : 0.7,
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  if (activeIndex !== index) {
                    e.currentTarget.style.opacity = "0.9";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeIndex !== index) {
                    e.currentTarget.style.opacity = "0.7";
                  }
                }}
              >
                {item.type === 'video' ? (
                  <div
                    style={{
                      width: "80px",
                      height: "60px",
                      borderRadius: "2px",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {(() => {
                      // Generate YouTube thumbnail URL
                      let thumbnailUrl = null;
                      if (item.videoType === 'youtube' && item.url) {
                        // Extract video ID (same logic as VideoEmbed)
                        const youtuBeMatch = item.url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
                        let videoId = null;
                        if (youtuBeMatch && youtuBeMatch[1]) {
                          videoId = youtuBeMatch[1].split('?')[0].split('&')[0];
                          if (videoId.length >= 11) {
                            videoId = videoId.substring(0, 11);
                          }
                        } else {
                          const watchMatch = item.url.match(/(?:watch\?v=|embed\/|v\/)([a-zA-Z0-9_-]{11})/);
                          if (watchMatch && watchMatch[1]) {
                            videoId = watchMatch[1];
                          } else {
                            const vParamMatch = item.url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
                            if (vParamMatch && vParamMatch[1]) {
                              videoId = vParamMatch[1];
                            }
                          }
                        }
                        
                        if (videoId) {
                          // Use maxresdefault for highest quality, fallback to hqdefault
                          thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
                        }
                      }
                      
                      return (
                        <>
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              alt={item.title || "Video thumbnail"}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                              loading="lazy"
                              onError={(e) => {
                                // Fallback to hqdefault if maxresdefault fails
                                const videoId = item.url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/)?.[1]?.split('?')[0]?.substring(0, 11) ||
                                                  item.url.match(/(?:watch\?v=|embed\/|v\/)([a-zA-Z0-9_-]{11})/)?.[1] ||
                                                  item.url.match(/[?&]v=([a-zA-Z0-9_-]{11})/)?.[1];
                                if (videoId) {
                                  e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                }
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                backgroundColor: "#000",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="white"
                                style={{ opacity: 0.8 }}
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          )}
                          {/* Play icon overlay */}
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              width: "32px",
                              height: "32px",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              pointerEvents: "none",
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <img
                    src={item.url}
                    alt={`${alt || "Project image"} thumbnail ${index + 1}`}
                    style={{
                      width: "80px",
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: "2px",
                      display: "block",
                    }}
                    loading="lazy"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-2 text-muted" style={{ fontSize: '0.95rem', opacity: 0.7 }}>
            {mediaItems[activeIndex]?.type === 'video' ? 'Video' : 'Image'} {activeIndex + 1} of {mediaItems.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;

